import { setSession, clearSession } from './session';

const HKDF_INFO = new TextEncoder().encode('summarizex-session-v1');

function toBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function fromBase64(b64: string): Uint8Array {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

export async function performHandshake(): Promise<void> {
  clearSession();

  // Step 1 — ECDH P-256 key pair
  const keyPair = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveBits']
  );

  // Step 2 — 32-byte random nonce
  const clientNonce = crypto.getRandomValues(new Uint8Array(32));

  // Step 3 — export public key as SPKI base64
  const clientPubSpki = await crypto.subtle.exportKey('spki', keyPair.publicKey);
  const clientPublicKey = toBase64(clientPubSpki);
  const clientNonceB64 = toBase64(clientNonce);

  // Step 4 — POST /crypto/handshake using raw fetch to avoid the Axios interceptors
  const baseURL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
  const reqHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    secretkey: process.env.REACT_APP_API_SECRET_KEY || '',
  };
  const token = localStorage.getItem('token');
  if (token) reqHeaders['Authorization'] = `Bearer ${token}`;

  const resp = await fetch(`${baseURL}/crypto/handshake`, {
    method: 'POST',
    headers: reqHeaders,
    body: JSON.stringify({ clientPublicKey, clientNonce: clientNonceB64 }),
  });

  if (!resp.ok) {
    throw new Error(`Handshake failed: HTTP ${resp.status}`);
  }

  const body = await resp.json();
  // Support both { data: { sessionId, ... } } and flat { sessionId, ... }
  const payload = body.data ?? body;
  const { sessionId, serverPublicKey: serverPubB64, expiresAt } = payload;

  // Step 5-6 — import server public key from SPKI base64
  const serverPublicKey = await crypto.subtle.importKey(
    'spki',
    fromBase64(serverPubB64),
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    []
  );

  // Step 7 — ECDH shared bits
  const sharedBits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: serverPublicKey },
    keyPair.privateKey,
    256
  );

  // Step 8 — HKDF-SHA256 → 256-bit AES-GCM key
  const hkdfBase = await crypto.subtle.importKey('raw', sharedBits, 'HKDF', false, ['deriveKey']);
  const aesKey = await crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: clientNonce, info: HKDF_INFO },
    hkdfBase,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );

  // Step 9 — store session in memory
  setSession({ sessionId, aesKey, expiresAt: new Date(expiresAt).getTime() });
}
